/**
 * DonForm — modale d'enregistrement d'un don avec sélecteur de type de donateur :
 *   - membre : recherche fuzzy parmi les membres de l'église choisie
 *   - anonyme : aucun champ supplémentaire (don sans nom)
 *   - organisation : nom + pays (texte libre)
 */
import { useState, useEffect, useMemo } from "react";
import Fuse from "fuse.js";
import Modal from "./Modal";
import Input from "./Input";
import Select from "./Select";
import Button from "./Button";
import Avatar from "./Avatar";
import { colors, fonts, goldGradient } from "../styles/tokens";

const DONOR_TYPES = [
  { id: "membre",       icon: "👤", label: "Membre" },
  { id: "anonyme",      icon: "🎭", label: "Anonyme" },
  { id: "organisation", icon: "🏛", label: "Organisation" },
];

const TYPES_DON = [
  { value: "dime",     label: "Dîme" },
  { value: "offrande", label: "Offrande" },
  { value: "projet",   label: "Don spécial / Projet" },
  { value: "mission",  label: "Mission" },
  { value: "autre",    label: "Autre" },
];

const METHODES = [
  { value: "especes",       label: "Espèces" },
  { value: "virement",      label: "Virement" },
  { value: "mobile_money",  label: "Mobile Money" },
  { value: "carte",         label: "Carte bancaire" },
  { value: "cheque",        label: "Chèque" },
];

const DEVISES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "XOF", label: "XOF (F CFA Ouest)" },
  { value: "XAF", label: "XAF (F CFA Centre)" },
  { value: "CAD", label: "CAD ($)" },
  { value: "CHF", label: "CHF" },
  { value: "USD", label: "USD ($)" },
];

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  type_donateur: "membre",
  // membre
  member_id: "",
  member_label: "",
  // organisation
  organisation_nom: "",
  organisation_pays: "",
  // commun
  montant: "",
  devise: "EUR",
  type_don: "dime",
  methode: "especes",
  date_don: today(),
  eglise_id: "",
  note: "",
};

export default function DonForm({
  open,
  onClose,
  onSubmit,
  members = [],
  eglises = [],
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  const [memberSearch, setMemberSearch] = useState("");
  const [showResults, setShowResults] = useState(false);

  // Reset à chaque ouverture
  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY });
    setErrors({});
    setGlobalError(null);
    setMemberSearch("");
    setShowResults(false);
  }, [open]);

  // Membres filtrés par église choisie (uniquement utile en mode "membre")
  const eligibleMembers = useMemo(() => {
    if (!form.eglise_id) return [];
    return members.filter((m) => m.eglise_id === Number(form.eglise_id));
  }, [members, form.eglise_id]);

  const fuse = useMemo(
    () => new Fuse(eligibleMembers, {
      keys: ["prenom", "nom", "full_name", "email", "telephone"],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 2,
    }),
    [eligibleMembers]
  );

  const searchResults = useMemo(() => {
    if (memberSearch.trim().length < 2) return [];
    return fuse.search(memberSearch.trim()).map((r) => r.item).slice(0, 6);
  }, [memberSearch, fuse]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    if (key === "eglise_id") {
      // Changement d'église → on retire le membre choisi
      setForm((prev) => ({ ...prev, eglise_id: value, member_id: "", member_label: "" }));
      setMemberSearch("");
    } else {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const setDonateurType = (type) => {
    setForm((prev) => ({
      ...prev,
      type_donateur: type,
      // Reset des champs spécifiques quand on change de type
      member_id: "",
      member_label: "",
      organisation_nom: "",
      organisation_pays: "",
    }));
    setMemberSearch("");
    setErrors({});
  };

  const pickMember = (m) => {
    setForm((prev) => ({
      ...prev,
      member_id: m.id,
      member_label: `${m.prenom} ${m.nom}`,
    }));
    setMemberSearch("");
    setShowResults(false);
    setErrors((prev) => ({ ...prev, member_id: null }));
  };

  const clearMember = () => {
    setForm((prev) => ({ ...prev, member_id: "", member_label: "" }));
    setMemberSearch("");
  };

  const validate = () => {
    const errs = {};

    // Selon le type de donateur
    if (form.type_donateur === "membre" && !form.member_id) {
      errs.member_id = "Sélectionnez un membre donateur";
    }
    if (form.type_donateur === "organisation" && !form.organisation_nom.trim()) {
      errs.organisation_nom = "Nom de l'organisation requis";
    }

    // Communs
    if (!form.eglise_id) errs.eglise_id = "Veuillez sélectionner une église";
    if (!form.date_don)  errs.date_don = "Date requise";
    if (form.date_don > today()) errs.date_don = "La date ne peut pas être dans le futur";
    if (!form.type_don)  errs.type_don = "Type requis";
    const montant = parseFloat(form.montant);
    if (!form.montant || isNaN(montant) || montant <= 0) {
      errs.montant = "Montant strictement positif requis";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setGlobalError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        type_donateur: form.type_donateur,
        eglise_id: Number(form.eglise_id),
        montant: parseFloat(form.montant),
        devise: form.devise,
        type_don: form.type_don,
        methode: form.methode,
        date_don: form.date_don,
        note: form.note.trim() || null,
        member_id: form.type_donateur === "membre" ? Number(form.member_id) : null,
        organisation_nom: form.type_donateur === "organisation" ? form.organisation_nom.trim() : null,
        organisation_pays: form.type_donateur === "organisation" ? (form.organisation_pays.trim() || null) : null,
      };
      const result = await onSubmit(payload);
      if (result?.ok === false) {
        setGlobalError(result.error || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      setGlobalError(err.message || "Erreur inattendue");
    } finally {
      setSubmitting(false);
    }
  };

  const eglisesOptions = eglises.map((e) => ({
    value: String(e.id),
    label: `${e.nom}${e.ville ? ` — ${e.ville}` : ""}`,
  }));

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Enregistrer un don"
      subtitle="Saisissez une nouvelle contribution financière"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            Enregistrer le don
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {globalError && (
          <div style={{
            background: "rgba(224,91,106,0.1)",
            border: `1px solid ${colors.rose}`,
            color: colors.rose,
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
          }}>
            {globalError}
          </div>
        )}

        {/* ─── Sélecteur de type de donateur ─── */}
        <div>
          <div style={{ fontSize: "13px", fontWeight: 500, color: colors.textMuted, marginBottom: "8px" }}>
            Qui fait ce don ? <span style={{ color: colors.gold }}>*</span>
          </div>
          <div style={{
            display: "flex", gap: "4px",
            background: colors.surface,
            padding: "4px",
            borderRadius: "10px",
            border: `1px solid ${colors.border}`,
          }}>
            {DONOR_TYPES.map((t) => {
              const active = form.type_donateur === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setDonateurType(t.id)}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: active ? 600 : 500,
                    color: active ? colors.bg : colors.text,
                    background: active ? goldGradient : "transparent",
                    transition: "all 200ms",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "16px" }}>{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Église destinataire (toujours visible) ─── */}
        <Select
          label="Église destinataire"
          value={form.eglise_id}
          onChange={set("eglise_id")}
          options={eglisesOptions}
          required
          error={errors.eglise_id}
          placeholder="Sélectionnez une église..."
        />

        {/* ─── Champs spécifiques au type ─── */}
        {form.type_donateur === "membre" && (
          <MemberSearch
            egliseSelected={!!form.eglise_id}
            memberLabel={form.member_label}
            memberSearch={memberSearch}
            setMemberSearch={setMemberSearch}
            showResults={showResults}
            setShowResults={setShowResults}
            searchResults={searchResults}
            pickMember={pickMember}
            clearMember={clearMember}
            error={errors.member_id}
          />
        )}

        {form.type_donateur === "anonyme" && (
          <div style={{
            background: colors.goldDim,
            border: `1px solid ${colors.goldBorder}`,
            borderRadius: "10px",
            padding: "12px 16px",
            color: colors.text,
            fontSize: "13px",
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
          }}>
            <span style={{ fontSize: "18px" }}>ℹ</span>
            <span>
              Ce don sera enregistré sans nom pour la transparence comptable.
              Le libellé affiché sera <strong>« Don anonyme »</strong>.
            </span>
          </div>
        )}

        {form.type_donateur === "organisation" && (
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "12px" }}>
            <Input
              label="Nom de l'organisation"
              value={form.organisation_nom}
              onChange={set("organisation_nom")}
              placeholder="Ex : Église Bethel Genève"
              required
              error={errors.organisation_nom}
              icon="🏛"
            />
            <Input
              label="Pays"
              value={form.organisation_pays}
              onChange={set("organisation_pays")}
              placeholder="Ex : Suisse"
            />
          </div>
        )}

        {/* ─── Montant + Devise ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "12px" }}>
          <Input
            label="Montant"
            type="number"
            step="0.01"
            min="0"
            value={form.montant}
            onChange={set("montant")}
            placeholder="0.00"
            required
            error={errors.montant}
            icon="💰"
          />
          <Select
            label="Devise"
            value={form.devise}
            onChange={set("devise")}
            options={DEVISES}
            placeholder={null}
          />
        </div>

        {/* ─── Type + Méthode ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <Select
            label="Type de don"
            value={form.type_don}
            onChange={set("type_don")}
            options={TYPES_DON}
            required
            error={errors.type_don}
            placeholder={null}
          />
          <Select
            label="Méthode de paiement"
            value={form.methode}
            onChange={set("methode")}
            options={METHODES}
            placeholder={null}
          />
        </div>

        {/* ─── Date ─── */}
        <Input
          label="Date du don"
          type="date"
          value={form.date_don}
          onChange={set("date_don")}
          required
          error={errors.date_don}
          max={today()}
        />

        {/* ─── Note ─── */}
        <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontFamily: fonts.body }}>
          <span style={{ fontSize: "13px", fontWeight: 500, color: colors.textMuted }}>
            Note / Commentaire
          </span>
          <textarea
            value={form.note}
            onChange={set("note")}
            rows={2}
            placeholder="Précision optionnelle (ex : projet d'évangélisation)"
            style={{
              background: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: "10px",
              padding: "12px 14px",
              fontSize: "14px",
              outline: "none",
              fontFamily: fonts.body,
              resize: "vertical",
              minHeight: "60px",
            }}
            onFocus={(e) => { e.target.style.borderColor = colors.gold; }}
            onBlur={(e)  => { e.target.style.borderColor = colors.border; }}
          />
        </label>

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/** Sous-composant : recherche fuzzy d'un membre. */
function MemberSearch({
  egliseSelected,
  memberLabel,
  memberSearch,
  setMemberSearch,
  showResults,
  setShowResults,
  searchResults,
  pickMember,
  clearMember,
  error,
}) {
  return (
    <div style={{ position: "relative" }}>
      <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontFamily: fonts.body }}>
        <span style={{ fontSize: "13px", fontWeight: 500, color: colors.textMuted }}>
          Membre donateur <span style={{ color: colors.gold }}>*</span>
        </span>

        {memberLabel ? (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            background: colors.bg,
            border: `1px solid ${colors.gold}`,
            borderRadius: "10px",
            padding: "8px 12px",
          }}>
            <Avatar name={memberLabel} size="sm" />
            <span style={{ flex: 1, fontWeight: 500 }}>{memberLabel}</span>
            <button
              type="button"
              onClick={clearMember}
              style={{ color: colors.textMuted, fontSize: "16px", padding: "0 6px", lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        ) : (
          <input
            type="text"
            value={memberSearch}
            onChange={(e) => { setMemberSearch(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            placeholder={egliseSelected ? "Rechercher un membre..." : "Sélectionnez d'abord une église"}
            disabled={!egliseSelected}
            style={{
              background: colors.bg,
              color: colors.text,
              border: `1px solid ${error ? colors.rose : colors.border}`,
              borderRadius: "10px",
              padding: "12px 14px",
              fontSize: "14px",
              outline: "none",
              fontFamily: fonts.body,
              opacity: egliseSelected ? 1 : 0.5,
            }}
          />
        )}
      </label>

      {showResults && memberSearch.trim().length >= 2 && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0, right: 0,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: "10px",
          boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
          zIndex: 20,
          overflow: "hidden",
        }}>
          {searchResults.length === 0 ? (
            <div style={{ padding: "12px 14px", color: colors.textMuted, fontSize: "13px" }}>
              Aucun membre trouvé.
            </div>
          ) : (
            searchResults.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => pickMember(m)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  width: "100%",
                  padding: "10px 14px",
                  background: "transparent",
                  borderBottom: `1px solid ${colors.border}`,
                  textAlign: "left",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = colors.goldDim; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <Avatar name={`${m.prenom} ${m.nom}`} size="sm" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "14px", fontWeight: 500 }}>{m.prenom} {m.nom}</div>
                  <div style={{ fontSize: "12px", color: colors.textMuted }}>
                    {m.cellule_nom || m.role_eglise || m.departement || "—"}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {error && (
        <div style={{ fontSize: "12px", color: colors.rose, marginTop: "4px" }}>{error}</div>
      )}
    </div>
  );
}
